import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n/I18nContext";

export const GROUP_MAX = 500;  // no cap on businesses per owner (each one paid); large lists: search + pages
const PAGE = 10;

// Same normalisation as the server (trials): lowercase, +tag stripped, Gmail dots stripped (googlemail = gmail).
const memberKey = (e) => {
  const [local = "", domain = ""] = e.trim().toLowerCase().split("@");
  const l = local.split("+")[0];
  return ["gmail.com", "googlemail.com"].includes(domain) ? `${l.replace(/\./g, "")}@gmail.com` : `${l}@${domain}`;
};
export const uniqueMembers = (list) => {
  const seen = new Set();
  return list.map((m) => m.trim().toLowerCase()).filter((m) => m && !seen.has(memberKey(m)) && seen.add(memberKey(m)));
};

// Editable list of member emails (group mini-site offer): 1 to `max` rows.
export const GroupMembersField = ({ members, onChange, max = GROUP_MAX, testId = "group-members" }) => {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [paste, setPaste] = useState("");
  const set = (i, v) => onChange(members.map((m, j) => (j === i ? v : m)));
  const idx = members.map((m, i) => i).filter((i) => !q || members[i].toLowerCase().includes(q.toLowerCase()));
  const pages = Math.max(1, Math.ceil(idx.length / PAGE));
  const shown = idx.slice(Math.min(page, pages - 1) * PAGE, Math.min(page, pages - 1) * PAGE + PAGE);
  const addMany = () => {
    const extra = paste.split(/[\s,;]+/).map((x) => x.trim()).filter((x) => x.includes("@"));
    onChange(uniqueMembers([...members.filter((m) => m.trim()), ...extra]).slice(0, max));
    setPaste("");
  };
  return (
    <div className="min-w-0 space-y-1.5" data-testid={testId}>
      {members.length > PAGE && (
        <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder={t("group.search", { n: members.length })} dir="ltr" className="h-8 bg-black/50 text-xs" data-testid={`${testId}-search`} />
      )}
      {shown.map((i) => [members[i], i]).map(([m, i]) => (
        <div key={i} className="flex min-w-0 items-center gap-1.5">
          <span className="w-5 shrink-0 text-end text-[0.7rem] tabular-nums text-neutral-500">{i + 1}</span>
          <Input type="email" value={m} onChange={(e) => set(i, e.target.value)} placeholder="email@exemple.com" dir="ltr" aria-label={t("group.memberN", { n: i + 1 })}
            className="h-9 min-w-0 flex-1 bg-black/50 text-sm" data-testid={`${testId}-input-${i}`} />
          {members.length > 1 && (
            <button type="button" onClick={() => onChange(members.filter((_, j) => j !== i))} aria-label={t("group.remove")}
              className="shrink-0 rounded-full p-1.5 text-red-300 hover:bg-white/5" data-testid={`${testId}-remove-${i}`}><X className="h-4 w-4" /></button>
          )}
        </div>
      ))}
      {pages > 1 && (
        <div className="flex items-center gap-2 text-xs text-neutral-400" data-testid={`${testId}-pager`}>
          <button type="button" className="rounded px-2 py-0.5 hover:bg-white/5 disabled:opacity-40" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid={`${testId}-prev`}>‹</button>
          <span className="tabular-nums">{Math.min(page, pages - 1) + 1}/{pages}</span>
          <button type="button" className="rounded px-2 py-0.5 hover:bg-white/5 disabled:opacity-40" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} data-testid={`${testId}-next`}>›</button>
        </div>
      )}
      <div className="flex min-w-0 gap-1.5">
        <Input value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={t("group.paste")} dir="ltr" className="h-8 min-w-0 flex-1 bg-black/50 text-xs" data-testid={`${testId}-paste`} />
        <button type="button" className="shrink-0 rounded-md border border-neutral-700 px-2 text-xs text-[var(--gold-bright)] disabled:opacity-40" disabled={!paste.includes("@")} onClick={addMany} data-testid={`${testId}-paste-add`}>{t("group.pasteAdd")}</button>
      </div>
      {members.length < max && (
        <button type="button" onClick={() => onChange([...members, ""])} className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-bright)] hover:text-white"
          data-testid={`${testId}-add`}><Plus className="h-3.5 w-3.5" /> {t("group.add", { n: members.length, max })}</button>
      )}
    </div>
  );
};

export const MemberList = ({ members, testId }) => {
  const { t } = useI18n();
  const [all, setAll] = useState(false);
  const shown = all ? members : members.slice(0, PAGE);
  return (
    <div className="min-w-0">
      <ul className={`min-w-0 space-y-0.5 ${all ? "max-h-72 overflow-y-auto" : ""}`} data-testid={testId}>
        {shown.map((m, i) => (
          <li key={m} className="flex min-w-0 items-baseline gap-1.5 text-xs text-neutral-200" data-testid={`${testId}-${i}`}>
            <span className="w-7 shrink-0 text-end tabular-nums text-neutral-500">{i + 1}</span><span className="min-w-0 break-all" dir="ltr">{m}</span>
          </li>
        ))}
      </ul>
      {members.length > PAGE && <button type="button" className="mt-1 text-xs text-[var(--gold-bright)]" onClick={() => setAll((x) => !x)} data-testid={`${testId}-toggle`}>
        {all ? t("group.showLess") : t("group.showAll", { n: members.length })}</button>}
    </div>
  );
};
